import { faCopy, faPlus, faTriangleExclamation, faX } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import React, { memo, useCallback, useContext, useMemo, useState } from 'react';

import NoteBody from '@/components/NoteBody';
import { NoteTypeSlugs } from '@/constants/noteTypes';
import AppContext from '@/contexts/appContext';
import NoteForm from '@/forms/NoteForm';
import useMostRecentNote from '@/hooks/useMostRecentNote';
import PersonContext from '@/pages/Person/PersonContext';
import SectionHeading from '@/pages/Profile/components/SectionHeading';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Spinner
} from '@/ui';
import Button from '@/ui/components/Button';
import DualTime from '@/ui/components/DualTime';
import formatStaffName from '@/utils/formatStaffName';
import { hasPermission, PEOPLE_AVAILABILITY_VIEW } from '@/utils/userPermission';

import AvailabilitySummarySection from '../AvailabilitySummarySection';

import { SectionState } from './types';

interface Props {
  showSummaryEditAction?: boolean;
}

const MemberAvailabilitySection = ({ showSummaryEditAction = true }: Props) => {
  const queryClient = useQueryClient();
  const { permissions } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const [sectionState, setSectionState] = useState<SectionState>(SectionState.VIEWING);
  const [copyingNote, setCopyingNote] = useState<boolean>(false);

  const { data: noteData, isPending } = useMostRecentNote(
    NoteTypeSlugs.MEMBER_AVAILABILITY,
    person.id
  );

  const note = noteData?.data;

  const handleCreateNewNote = useCallback(() => {
    setCopyingNote(false);
    setSectionState(SectionState.CREATING);
  }, []);

  const handleNoteFormSuccess = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['getMostRecentNote', NoteTypeSlugs.MEMBER_AVAILABILITY, person.id]
    });
    setSectionState(SectionState.VIEWING);
  }, [person.id, queryClient]);

  const renderedNote = useMemo(() => {
    if (!note?.body) return null;

    return (
      <article className="mb-2">
        <NoteBody note={note} />
      </article>
    );
  }, [note]);

  const renderState = useCallback(() => {
    if (sectionState === SectionState.CREATING) {
      return (
        <div className="relative">
          <Button
            ariaLabel="Cancel note creation"
            className="absolute top-4 right-0"
            color="white"
            onClick={() => setSectionState(SectionState.VIEWING)}
            size="xs">
            <FontAwesomeIcon icon={faX} />
          </Button>
          <NoteForm
            initialNote={copyingNote ? note?.body : undefined}
            noteTypeSlug={NoteTypeSlugs.MEMBER_AVAILABILITY}
            objectId={person.id}
            withHeading={false}
            successCallback={handleNoteFormSuccess}
          />
        </div>
      );
    }

    return (
      <div className="relative">
        {note && (
          <div className="mb-3 flex items-center gap-3 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-yellow-600" />
            <span>
              This is a free-text note and may not accurately reflect the member&apos;s current
              availability.
            </span>
          </div>
        )}

        {renderedNote}

        <div className="flex justify-between items-center">
          <div className="w-3/5">
            {note?.author && (
              <p className="text-xs text-gray-500">
                Created{' '}
                <DualTime dateTime={new Date(note.created_at)} memberTZ={person.time_zone} />
                <span> by {formatStaffName({ staff: note.author })}</span>
              </p>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger aria-label="New Note">
              <Button color="white" size="sm">
                New Note
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem icon={faPlus} onClick={handleCreateNewNote}>
                Create New
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={faCopy}
                onClick={() => {
                  setCopyingNote(true);
                  setSectionState(SectionState.CREATING);
                }}>
                Copy and Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }, [
    copyingNote,
    handleCreateNewNote,
    handleNoteFormSuccess,
    note,
    person.id,
    person.time_zone,
    renderedNote,
    sectionState
  ]);

  return (
    <div id="member-availability-section" className="relative">
      <div className="mb-3">
        <SectionHeading text="Member Availability" />
      </div>
      {hasPermission({ permissions, requiredPermission: PEOPLE_AVAILABILITY_VIEW }) && (
        <AvailabilitySummarySection
          showEditAction={showSummaryEditAction && sectionState === SectionState.VIEWING}
          onNewNoteClick={handleCreateNewNote}
        />
      )}
      {isPending ? (
        <Spinner />
      ) : note || sectionState === SectionState.CREATING ? (
        <Accordion
          key={`member-availability-notes-${sectionState}`}
          variant="ghost"
          className="mt-2">
          <AccordionItem defaultOpen={sectionState !== SectionState.VIEWING} className="border-0">
            <AccordionTrigger className="px-0 py-0 text-sm font-medium text-blue-600 hover:bg-transparent hover:underline dark:text-blue-400">
              Show note
            </AccordionTrigger>
            <AccordionContent className="bg-transparent px-0 pb-0 pt-2">
              {renderState()}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : null}
    </div>
  );
};

export default memo(MemberAvailabilitySection);
